import { ToWords } from "to-words";
import DownloadButton from "../DownloadButton";
import {
  capitalizeFirstLetterOfEachWord,
  convertAmountToWords,
} from "../../utils/Formatter";
import ProfilePlaceholder from "../../assets/svgIcons/ProfilePlaceholder.svg";
import "../../styles/components/transactionPrintLayouts/POInvoiceLayout.css";

export const JobWorkInvoiceLayout = ({
  headDetails,
  companyData,
  itemDetails,
  rowData,
  invoiceTableData,
  selectedReportName,
}) => {
  const toWords = new ToWords();
  const path = window.location.pathname.split("/").filter(Boolean);
  const transactionName = selectedReportName || path[1];

  const organizationData = JSON.parse(localStorage.getItem("organisation"));

  const formatDate = (dateStr) => {
    if (!dateStr) return "--";
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };
  const details = [
    {
      label: "Jobwork PO Date",
      value: formatDate(headDetails.po_date),
    },
    { label: "Jobwork PO No", value: headDetails.po_no },
  ];

  const purchaseDetails = [
    { label: "Jobwork Invoice No", value: headDetails.tran_no },
    {
      label: "Jobwork Invoice Date",
      value: formatDate(headDetails.date || headDetails.created_at),
    },
  ];

  const notes = [
    "Test certificate / inspection report must accompany material.",
    "Guarantee / Warranty certificates",
    "Please courier original MODVAT copies of invoices directly to us & not with transporter.",
  ];

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return "0.00";
    return Number(amount).toFixed(2);
  };

  const formatCompanyAddress = (companyData) => {
    if (!companyData) return "N/A";

    const parts = [
      companyData.address?.trim(),
      companyData.city?.trim(),
    ].filter(Boolean);

    const pin = companyData.pincode ? `- ${companyData.pincode}` : "";

    return parts.length > 0 || pin
      ? `${parts.join(", ")} ${pin}`.trim()
      : "N/A";
  };

  const visibleFields = [...rowData]
    .filter((field) => !field.is_internal_field)
    .reverse();

  const calculateTaxFromItems = () => {
    if (!itemDetails || !Array.isArray(itemDetails)) {
      return {
        cgst: 0,
        sgst: 0,
        igst: 0,
        totalGst: 0,
        cgstPercent: 0,
        sgstPercent: 0,
        igstPercent: 0,
      };
    }

    const taxTotals = itemDetails.reduce(
      (totals, item) => {
        const cgstAmt = parseFloat(item.cgst_amt || 0);
        const sgstAmt = parseFloat(item.sgst_amt || 0);
        const igstAmt = parseFloat(item.igst_amt || 0);
        const cgstPer = parseFloat(item.cgst_per || 0);
        const sgstPer = parseFloat(item.sgst_per || 0);
        const igstPer = parseFloat(item.igst_per || 0);

        return {
          cgst: totals.cgst + cgstAmt,
          sgst: totals.sgst + sgstAmt,
          igst: totals.igst + igstAmt,
          cgstPer: totals.cgstPer + cgstPer,
          sgstPer: totals.sgstPer + sgstPer,
          igstPer: totals.igstPer + igstPer,
        };
      },
      { cgst: 0, sgst: 0, igst: 0, cgstPer: 0, sgstPer: 0, igstPer: 0 }
    );

    const totalGst = taxTotals.cgst + taxTotals.sgst + taxTotals.igst;

    const itemCount = itemDetails.length;
    const cgstPercent =
      itemCount > 0 ? (taxTotals.cgstPer / itemCount).toFixed(2) : 0;
    const sgstPercent =
      itemCount > 0 ? (taxTotals.sgstPer / itemCount).toFixed(2) : 0;
    const igstPercent =
      itemCount > 0 ? (taxTotals.igstPer / itemCount).toFixed(2) : 0;

    return {
      cgst: taxTotals.cgst.toFixed(2),
      sgst: taxTotals.sgst.toFixed(2),
      igst: taxTotals.igst.toFixed(2),
      totalGst: totalGst.toFixed(2),
      cgstPercent,
      sgstPercent,
      igstPercent,
    };
  };

  const calculateTotalFromItems = () => {
    if (!itemDetails || !Array.isArray(itemDetails)) {
      return 0;
    }

    const totalAmount = itemDetails.reduce((sum, item) => {
      const amount = parseFloat(item.amount || 0);
      return sum + amount;
    }, 0);

    return totalAmount;
  };

  const { cgst, sgst, igst, totalGst, cgstPercent, sgstPercent, igstPercent } =
    calculateTaxFromItems();
  const totalAmountFromItems = calculateTotalFromItems();
  const finalTotal = totalAmountFromItems + parseFloat(totalGst);
  const amountInWords = convertAmountToWords(finalTotal, toWords);

  return (
    <div className="invoice-container">
      <h2 className="invoice-title" style={{ textAlign: "center" }}>
        {capitalizeFirstLetterOfEachWord(transactionName || "")}
      </h2>

      <div className="invoice-box">
        <div className="invoice-left">
          <div className="invoice-logo-container">
            <img
              src={organizationData.logo_url || ProfilePlaceholder}
              alt="Logo"
              className="invoice-logo-content"
            />
          </div>
          <div>
            <h3 className="invoice-company-name">
              {organizationData.name || "N/A"}
            </h3>
            <p className="invoice-company-address">
              {formatCompanyAddress(organizationData) || "N/A"}
              <br />
              GSTIN: {organizationData.gst_no || "N/A"}
              <br />
              State: {organizationData.state || "N/A"}
            </p>
          </div>
        </div>

        <div className="invoice-right">
          {purchaseDetails.map((item, index) => (
            <div
              key={index}
              className={`purchase-detail ${
                index === 0 ? "border-bottom" : ""
              }`}
            >
              <p className="detail-label">
                <strong>{item.label}:</strong>
              </p>
              <div className="detail-value">{item.value || "N/A"}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="invoice-supplier-details-container">
        <div className="invoice-supplier-section">
          <h4 className="invoice-section-title">Name of Supplier:</h4>
          <p className="invoice-section-text">
            <span style={{ fontSize: "14px", fontWeight: "bolder" }}>
              {companyData.company_name || "N/A"}
            </span>
            <br />
            Address: {companyData.company_address || "N/A"}
            <br />
            State: Karnataka
          </p>
        </div>

        <div className="invoice-delivery-section">
          <h4 className="invoice-section-title">Delivery at:</h4>
          <p className="invoice-section-text">
            <span style={{ fontSize: "14px", fontWeight: "bolder" }}>
              {companyData.company_name || "N/A"}
            </span>
            <br />
            Address: {companyData.company_address || "Address not specified"}
            <br />
            GSTIN: {companyData.company_gst_no || "N/A"}
            <br />
            State: Karnataka
          </p>
        </div>
      </div>

      <div className="invoice-overflow-x-scroll">
        <table className="invoice-table">
          <thead className="invoice-thead">
            <tr>
              <th className="invoice-th" style={{ borderLeftWidth: "0px" }}>
                No.
              </th>
              {visibleFields.map((field, index) => (
                <th
                  key={index}
                  className="invoice-th"
                  style={
                    index === visibleFields.length - 1
                      ? { borderRightWidth: "0px" }
                      : {}
                  }
                >
                  {field.title || field.field_name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {itemDetails.map((item, index) => (
              <tr key={index}>
                <td className="invoice-td" style={{ borderLeftWidth: "0px" }}>
                  {index + 1}
                </td>
                {[...rowData]
                  .filter((field) => !field.is_internal_field)
                  .reverse()
                  .map((field, idx, arr) => (
                    <td
                      key={idx}
                      className="invoice-td"
                      style={
                        idx === arr.length - 1
                          ? { borderRightWidth: "0px" }
                          : {}
                      }
                    >
                      {item[field.field_name] || "-"}
                    </td>
                  ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="invoice-grid-container">
        <div className="invoice-left-pane">
          <p className="invoice-section-heading">
            <strong>Terms & Conditions</strong>
          </p>
          {details.map((item, index) => (
            <div key={index} className="invoice-detail-row">
              <p className="invoice-detail-label">{item.label}:</p>
              <div className="invoice-detail-value">{item.value || "--"}</div>
            </div>
          ))}
        </div>
        <div className="invoice-right-pane">
          {[
            {
              label: "Net Amount (Before GST)",
              value: formatCurrency(totalAmountFromItems || 0),
            },
            { label: `CGST @ ${cgstPercent}%`, value: formatCurrency(cgst) },
            { label: `SGST @ ${sgstPercent}%`, value: formatCurrency(sgst) },
            { label: `IGST @ ${igstPercent}%`, value: formatCurrency(igst) },
          ].map((row, idx, arr) => (
            <div
              className={`invoice-summary-row ${
                idx === arr.length - 1 ? "no-border" : ""
              }`}
              key={idx}
            >
              <p className="invoice-summary-label">
                <strong>{row.label}:</strong>
              </p>
              <div className="invoice-summary-value">{row.value || "-"}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="invoice-grid-container no-top-border">
        <div className="invoice-left-pane note-pane">
          <p
            className="invoice-note-heading"
            style={{
              paddingLeft: "16px",
              paddingRight: "16px",
              paddingTop: "16px",
            }}
          >
            <strong>Note: </strong>
          </p>
          <ul className="invoice-note-list">
            {notes.map((note, index) => (
              <li key={index} className="invoice-note-item">
                {note}
              </li>
            ))}
          </ul>
          <p className="invoice-total-amount">
            <strong>Total Amount: </strong>
            {headDetails?.net_amt ? `${amountInWords} Only` : "N/A"}
          </p>
        </div>

        <div className="invoice-right-pane">
          <div className="invoice-summary-row">
            <p className="invoice-summary-label">
              <strong>Total GST Amount:</strong>
            </p>
            <div className="invoice-summary-value">{totalGst}</div>
          </div>
          <div className="invoice-summary-row">
            <p className="invoice-summary-label">
              <strong>Total:</strong>
            </p>
            <div className="invoice-summary-value">
              {formatCurrency(finalTotal)}
            </div>
          </div>
          <div className="invoice-footer-signature">
            <p style={{ margin: "0px", fontSize: "13px" }}>
              <strong>For, {organizationData.name}</strong>
            </p>
          </div>
        </div>
      </div>
      <p className="invoice-system-generated-note">
        This is a system-generated document and does not require a signature.
      </p>
      <div className="invoice-buttons" style={{ paddingTop: "40px" }}>
        <DownloadButton
          tableData={invoiceTableData}
          fileName="invoice"
          downloadFormats={["excel", "pdf", "full-page-pdf"]}
          fullPageDownload={true}
          fullPageSelector=".enquiry-page-container"
        />
      </div>
    </div>
  );
};
